import React, { useState, useEffect, Fragment, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { makeStyles } from "@mui/styles";
import { useStores } from "../../../core/stores/index";
import "./AddUser.css";
import { Button, Select, MenuItem, SelectChangeEvent } from "@mui/material";
import { IUser } from "common/interfaces/IUser";
//import Select from "react-select";
import { Options } from "react-select/dist/declarations/src";
import { useNavigate, useParams } from "react-router-dom";
import { ISaveUserRequest } from "common/interfaces/apiRequest/SaveUserRequest";
import { UserType } from "common/enums/UserType";
import { Role } from "common/enums/Role";
import { useSnackbar } from "notistack";
import { IUpdateUserRequest } from "common/interfaces/apiRequest/IUpdateUserRequest";
import { IDelegationUser } from "common/interfaces/IDelegationUser";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { DateHelper } from "common/helpers/DateHelper";
import { IADUser } from "common/interfaces/IADUser";
import { IUserAudit } from "common/interfaces/IUserAudit";
import { Audit } from "components/common/Audit";

interface AddUserProp {
    children: JSX.Element;
    user?: IUser;
}

export const AddUser = observer((props: AddUserProp) => {
    const { userStore } = useStores();
    const navigate = useNavigate();
    const snackbar = useSnackbar();

    const { userId } = useParams() as { userId: string };
    const [selectedUser, setSelectedUser] = useState<IUser | undefined>(userStore.selectedUser);

    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [showAudit, setShowAudit] = useState<boolean>(false);

    useEffect(() => {
        if (userStore.newUser) {
            setUserName(userStore.newUser.name);
            setEmail(userStore.newUser.email);
            setIsLoading(false);
        }
        if (userStore.selectedUser) {
            setSelectedUser(userStore.selectedUser);
            setUserName(userStore.selectedUser.name);
            setEmail(userStore.selectedUser?.email);
            setUserEnabled(userStore.selectedUser?.isEnabled);
            setUserRoles(userStore.selectedUser?.roles as string[]);
            setUserTypeSelection(userStore.selectedUser?.userType);
            setSelectedDelegationUser(getSelectedDelegationUser(selectedUser?.delegationUserId as number));
            setDelegationEndDate(getDateFormatted(userStore.selectedUser.delegationEndDate));
            console.log(selectedUser);
            setIsLoading(false);
        }
    }, [userStore.newUser, userStore.selectedUser]);

    const getDateFormatted = (dateString: string | undefined) => {
        if (DateHelper.isValidDateOrString(dateString)) {
            return DateHelper.dateToInternationalString(new Date(dateString as string));
        } else {
            return undefined;
        }
    };

    const [isNameValid, setIsNameValid] = useState<boolean>(true);
    const [isUserTypeValid, setIsUserTypeValid] = useState<boolean>(true);
    const [isDelegationUserValid, setIsDelegationUserValid] = useState<boolean>(true);
    const [isDelegationEndDateValid, setIsDelegationEndDateValid] = useState<boolean>(true);

    const [userTypeSelection, setUserTypeSelection] = React.useState<UserType | string | undefined>(selectedUser?.userType);

    const [userEnabled, setUserEnabled] = useState<boolean | undefined>(selectedUser?.isEnabled);

    const [userName, setUserName] = useState<string | undefined>(selectedUser?.name);

    const [email, setEmail] = useState<string | undefined>(selectedUser?.email);

    const [isDelegationActive, setIsDelegationActive] = useState<boolean | undefined>(selectedUser?.isDelegationActive);

    const [userRoles, setUserRoles] = useState<string[]>(selectedUser?.roles ? (selectedUser.roles as string[]) : []);

    const [delegationEndDate, setDelegationEndDate] = useState<string | undefined>(getDateFormatted(selectedUser?.delegationEndDate));

    const getSelectedDelegationUser = (delegationUserId: number): IDelegationUser | undefined => {
        if (delegationUserId) {
            if (userStore.users.length > 0) {
                const delegationUser = userStore.users.filter((x) => x.userId == delegationUserId)[0];
                return { name: delegationUser.name as string, userId: delegationUser.userId as number };
            }
        }
    };

    const [selectedDelegationUser, setSelectedDelegationUser] = useState<IDelegationUser | undefined>(
        selectedUser?.delegationUserId ? getSelectedDelegationUser(selectedUser?.delegationUserId) : undefined
    );

    const onUserNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUserName(e.target.value);
        setIsNameValid(e.target.value != "");
    };

    const onRolesCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setUserRoles([...userRoles, e.target.value]);
        } else {
            if (userRoles.includes(e.target.value)) {
                const alteredArray = userRoles.filter((x) => x != e.target.value);
                setUserRoles(alteredArray);
            }
        }
        console.log(userRoles);
    };

    const checkboxSelection = (value: string): boolean => {
        return userRoles?.length > 0 ? userRoles.includes(value) : false;
    };

    const handleUserTypeSelectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setUserTypeSelection(e.target.value as string);
    };

    const [delegationUsersList, setDelegationUsersList] = useState<IDelegationUser[]>([]);

    const [adUsersList, setAdUsersList] = useState<IADUser[]>([]);

    useEffect(() => {
        setDelegationUsersList(
            userStore.users.map<IDelegationUser>((x) => {
                return { name: x.name as string, userId: x.userId as number };
            })
        );
    }, []);

    const onDelegationUserSearchChange = (event: any, value: any) => {
        console.log(value);
        setIsDelegationUserValid(true);
        setSelectedDelegationUser(value);
    };

    const onDelegationEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDelegationEndDate(e.target.value);
        setIsDelegationEndDateValid(true);
    };

    const onCloseHandle = () => {
        userStore.newUser = {};
        userStore.selectedUser = {};
        navigate("/users");
    };

    const onSaveHandle = async (e: any) => {
        if (validate()) {
            let response: any;
            if (selectedUser?.userId) {
                const updateUserRequest: IUpdateUserRequest = {
                    userId: selectedUser.userId,
                    name: userName as string,
                    isEnabled: userEnabled as boolean,
                    userType: userTypeSelection as string,
                    roles: userRoles,
                    isDelegationActive: isDelegationActive as boolean,
                    delegationUserId: Number(selectedDelegationUser?.userId),
                    delegationEndDate: delegationEndDate ? delegationEndDate : null,
                };
                response = await userStore.updateUser(updateUserRequest);
            } else {
                const saveUserRequest: ISaveUserRequest = {
                    name: userName as string,
                    isEnabled: userEnabled as boolean,
                    email: email,
                    userType: userTypeSelection as string,
                    roles: userRoles,
                    isDelegationActive: isDelegationActive as boolean,
                    delegationUserId: Number(selectedDelegationUser?.userId),
                    delegationEndDate: delegationEndDate ? delegationEndDate : null,
                };

                console.log(saveUserRequest);

                response = await userStore.saveUser(saveUserRequest);
            }

            userStore.users = [];

            navigate("/users");
        } else {
            console.log("Validation failed");
        }
    };

    const validate = () => {
        let isValid = true;
        isValid = userName ? true : false;
        setIsNameValid(isValid);
        if (!isValid) return false;
        isValid = userTypeSelection ? true : false;
        setIsUserTypeValid(isValid);
        if (!isValid) return false;
        if (isDelegationActive) {
            isValid = selectedDelegationUser ? true : false;
            setIsDelegationUserValid(isValid);
            if (!isValid) return false;
            isValid = delegationEndDate ? true : false;
            setIsDelegationEndDateValid(isValid);
            if (!isValid) return false;
        }
        return isValid;
    };

    const delegationOnChangeHandler = (value: boolean) => {
        setIsDelegationActive(value);
        if (value == false) {
            setDelegationEndDate(undefined);
            setSelectedDelegationUser(undefined);
        }
    };

    const [auditEntries, setAuditEntries] = useState<IUserAudit[]>([]);

    const getUserAuditEntries = async () => {
        userStore.getUserAudit(selectedUser?.userId as number).then((data) => {
            setAuditEntries(data);
            setShowAudit(true);
        });
    };

    const getColumns = () => {
        const columns = [
            {
                key: "actionDate",
                name: "Date",
                renderCell: (params: any) => 
                    <div>{DateHelper.getShortTextDate(params.row.actionDate,true,false)}</div>
                ,
            },
            {
                key: "user",
                name: "User",
                renderCell: (params: any) => <div>{params.row.user}</div>,
            },
            {
                key: "actionName",
                name: "Action",
                renderCell: (params: any) => <div>{params.row.actionName}</div>,
            },
            {
                key: "detail",
                name: "Detail",
                renderCell: (params: any) => <div>{params.row.detail}</div>,
            },
        ];

        return columns;
    };

    const onAuditCloseHandle = () => {
        setAuditEntries([]);
        setShowAudit(false);
    };

    return (
        <div className="container-fluid p-1">
            <div className="m-4 card">
                <div className="card-header">
                    <div className="row">
                        <div className="col-6 align-content-center">
                            <h1>User Configuration</h1>
                        </div>
                    </div>
                </div>
                {!isLoading && !showAudit && (
                    <div className="row card-body w-100 h-100">
                        <form className="needs-validation">
                            <div className="form-group row pb-2">
                                <label htmlFor="name" className="col-md-2 col-form-label">
                                    Name
                                </label>
                                <div className="col-md-4">
                                    <input
                                        type="name"
                                        name="name"
                                        disabled
                                        required
                                        value={userName}
                                        onChange={onUserNameChange}
                                        className={isNameValid ? "form-control" : "form-control is-invalid"}
                                        id="inputName"
                                        placeholder="Name"
                                    />
                                </div>
                            </div>
                            <div className="form-group row pb-2">
                                <label htmlFor="userEnabled" className="col-sm-2 col-form-label">
                                    User Enabled
                                </label>
                                <div className="col-md-4">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            value={"enabled"}
                                            type="radio"
                                            checked={userEnabled ? true : false}
                                            onClick={() => setUserEnabled(true)}
                                            name="userEnabled"
                                            id="flexRadioDefault1"
                                        />
                                        <label className="form-check-label" htmlFor="flexRadioDefault1">
                                            Enabled
                                        </label>
                                    </div>
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            value={"disabled"}
                                            type="radio"
                                            checked={userEnabled ? false : true}
                                            onClick={() => setUserEnabled(false)}
                                            name="userEnabled"
                                            id="flexRadioDefault2"
                                        />
                                        <label className="form-check-label" htmlFor="flexRadioDefault2">
                                            Disabled
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="form-group row pb-2">
                                <label htmlFor="userType" className="col-md-2 col-form-label">
                                    User Type
                                </label>
                                <div className="col-md-4">
                                    <select
                                        value={userTypeSelection}
                                        required
                                        onChange={(e) => handleUserTypeSelectionChange(e)}
                                        className={isUserTypeValid ? "form-control" : "form-control is-invalid"}
                                        name="userType"
                                    >
                                        <option selected={true} value={""}>
                                            Select User Type
                                        </option>
                                        <option value={"Reader"}>Reader</option>
                                        <option value={"Editor"}>Editor</option>
                                        <option value={"IT Admin"}>IT Admin</option>
                                        <option value={"Application Admin"}>Application Admin</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group row pb-2">
                                <label htmlFor="userType" className="col-md-2 col-form-label">
                                    User Roles
                                </label>
                                <div className="col-md-4 form-check checkbox-group-padding">
                                    <ul className="list-group">
                                        <li className="list-group-item">
                                            <input
                                                type="checkbox"
                                                checked={checkboxSelection("Customer Services")}
                                                onChange={onRolesCheck}
                                                value={"Customer Services"}
                                                name="customerservices"
                                            />{" "}
                                            Customer Services
                                        </li>
                                        <li className="list-group-item">
                                            <input
                                                type="checkbox"
                                                checked={checkboxSelection("Raise Claims")}
                                                onChange={onRolesCheck}
                                                name="raiseclaims"
                                                value={"Raise Claims"}
                                            />{" "}
                                            Raise Claims
                                        </li>
                                        <li className="list-group-item">
                                            <input
                                                type="checkbox"
                                                checked={checkboxSelection("View Standard Cost")}
                                                onChange={onRolesCheck}
                                                name="viewstandardcost"
                                                value={"View Standard Cost"}
                                            />{" "}
                                            View Standard Cost
                                        </li>
                                        <li className="list-group-item">
                                            <input
                                                type="checkbox"
                                                checked={checkboxSelection("Financial Transaction Manager")}
                                                onChange={onRolesCheck}
                                                name="financialtransactionmanager"
                                                value={"Financial Transaction Manager"}
                                            />{" "}
                                            Financial Transaction Manager
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="form-group row pb-2">
                                <label htmlFor="enabled" className="col-sm-2 col-form-label">
                                    Delegation Active
                                </label>
                                <div className="col-md-4">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            checked={isDelegationActive ? true : false}
                                            onClick={() => delegationOnChangeHandler(true)}
                                            type="radio"
                                            name="delegationActive"
                                            id="flexRadioDefault1"
                                        />
                                        <label className="form-check-label" htmlFor="flexRadioDefault1">
                                            Yes
                                        </label>
                                    </div>
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            checked={isDelegationActive ? false : true}
                                            onClick={() => delegationOnChangeHandler(false)}
                                            name="delegationActive"
                                            id="flexRadioDefault2"
                                        />
                                        <label className="form-check-label" htmlFor="flexRadioDefault2">
                                            No
                                        </label>
                                    </div>
                                </div>
                            </div>
                            {isDelegationActive && (
                                <>
                                    <div className="form-group row pb-2">
                                        <label htmlFor="enabled" className="col-sm-2 col-form-label">
                                            Delegate To User
                                        </label>
                                        <div className="col-md-4">
                                            <Autocomplete
                                                disablePortal
                                                title={"Delegation User"}
                                                value={selectedDelegationUser}
                                                onChange={(event, value) => onDelegationUserSearchChange(event, value)}
                                                renderInput={(params) => <TextField {...params} />}
                                                options={delegationUsersList}
                                                componentsProps={{ popper: { style: { width: "fit-content", height: "100px" } } }}
                                                getOptionKey={(option: IDelegationUser) => option.userId}
                                                getOptionLabel={(option: IDelegationUser) => option.name}
                                            />
                                            <div className={isDelegationUserValid ? "invalid-feedback" : "invalid-feedback invalid-div-block"}>
                                                Select a user for delegation.
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-group row pb-2">
                                        <label htmlFor="enabled" className="col-sm-2 col-form-label">
                                            Deleg. End Date
                                        </label>
                                        <div className="col-md-4">
                                            <input
                                                required
                                                onChange={onDelegationEndDateChange}
                                                value={delegationEndDate}
                                                type="date"
                                                placeholder="dd/mm/yyyy"
                                                className={isDelegationEndDateValid ? "form-control" : "form-control is-invalid"}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            <div className="form-group row button-row-margin">
                                <div className="col-md-12">
                                    <button className="btn btn-primary" onClick={onSaveHandle} type="button">
                                        Save
                                    </button>
                                    <a className="btn btn-primary ms-1" onClick={onCloseHandle}>
                                        Close
                                    </a>
                                    <button type="button" className="btn btn-primary float-end d-inline" onClick={() => getUserAuditEntries()}>
                                        Audit Trail
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                <div className="row card-body w-100 h-100">
                    {auditEntries.length > 0 && showAudit && <Audit columns={getColumns()} rows={auditEntries} />}
                    {showAudit && (
                        <div className="form-group row button-row-margin">
                            <div className="col-md-12">
                                <a className="btn btn-primary float-end d-inline" onClick={() => onAuditCloseHandle()}>
                                    Close
                                </a>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});
